import React from "react";
import { Flex, Text, Link, Button } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { ControlTowerContext } from "#utils/context";
import { formatShortFormDate } from "#utils/datetime";
import { SPACING } from "#utils/spacing";
import { useControlTowerContext } from "../ControlTowerContext";
import { formatCustomerName, formatTicketStatus } from "../utils";
import * as styles from "./styles_vanilla.css";
import { WorkflowDetailsHeaderProps } from "./types";

export const WorkflowDetailsHeader: React.FC<WorkflowDetailsHeaderProps> = ({
  workflow,
  onClose,
}) => {
  const context = useControlTowerContext();
  const isCanopy = context === ControlTowerContext.CANOPY;
  const displayStatus = isCanopy
    ? formatTicketStatus(workflow.status)
    : (workflow.statusCategoryName ?? formatTicketStatus(workflow.status));
  const showStatusIcon = workflow.status === "IN_PROGRESS";

  const customers = workflow.customers || [];
  const formattedCustomer = customers.length > 0 ? formatCustomerName(customers[0]) : null;

  const breadcrumbItems = [workflow.workType, formattedCustomer, workflow.entityName].filter(
    Boolean,
  );

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM}>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="h2" data-testid="workflow-description">
            {workflow.description}
          </Text>
          <Flex alignItems="center" gap={SPACING.SMALL}>
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Text variant="secondary-md" color={Colors.Neutral500}>
                    |
                  </Text>
                )}
                <Text variant="secondary-md" color={Colors.Neutral600}>
                  {item}
                </Text>
              </React.Fragment>
            ))}
            {breadcrumbItems.length > 0 && (
              <Text variant="secondary-md" color={Colors.Neutral500}>
                |
              </Text>
            )}
            {isCanopy ? (
              <Link
                href={workflow.jiraLink}
                shouldOpenInNewTab
                icon="external-link"
                iconPosition="right"
                iconVariant="outline"
                iconSize="small"
              >
                {workflow.key}
              </Link>
            ) : (
              <Text variant="secondary-md" color={Colors.Neutral600}>
                {workflow.key}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex gap={SPACING.X_SMALL} alignItems="center">
          <Button
            className={styles.iconButton}
            onClick={onClose}
            data-testid="workflow-close-button"
            aria-label="Close"
            icon="times"
            variant="neutral"
          />
        </Flex>
      </Flex>

      <Flex gap={SPACING.XXX_LARGE}>
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Flex alignItems="center" gap={SPACING.X_SMALL}>
            {showStatusIcon && <Icon type="sync-alt" color={Colors.Neutral900} />}
            <Text variant="regular-md" data-testid="workflow-status">
              {displayStatus}
            </Text>
          </Flex>
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Status
          </Text>
        </Flex>

        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="regular-md" data-testid="workflow-created-date">
            {formatShortFormDate(workflow.createdAt)}
          </Text>
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Created date
          </Text>
        </Flex>

        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="regular-md" data-testid="workflow-assignee">
            {workflow.assignedTo?.displayName || "Unassigned"}
          </Text>
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Assigned to
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};
